import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClassNames } from '@kufox/mui/emotion';
import { queryPersistence } from '@kc/gbiz-base/lib/QueryPersistence';
import useResponsive from '@kufox/mui/hooks/useResponsive';
import { MenuOutlined } from '@kufox/icons';
import { includes, throttle } from 'lodash';
import useTheme from '@kufox/mui/hooks/useTheme';
import clsx from 'clsx';
import Headroom from './HeadRoom';
import NavLink from './Nav/Nav';
import NavUser from './NavUser';
import CmsComs from '../CmsComs';
import { namespace } from '../CmsComs/model';
import { namespace as headerModal } from './model';
import MenuDrawer from './MenuDrawer';
import I18nDrawer from './I18nDrawer';
import { checkIsSub, gaClickNew, getGaElement, kcsensorsClick } from '../common/tools';
import LoadStyle from '../CmsComs/LoadStyle';
import { Nav, Links, NavUserDom, LogoLink, MenuBox, NavLinks } from './styled';

const DEFAULT_NAVWIDTH_MAP = {
  INNER_OFFSET: 40, // 提前计算偏移量
  NAV_LINK: 600, // 默认菜单宽度
  SIGNS: 200, // 默认登陆注册宽度
  ACTION_BOX: 132, // 默认下载/语言/汇率总宽度
  MARGIN: 80, // 盒子总间距
};

/**
 * 登录状态4种
 *   logo始终展示
 *   状态1:显示全部图标（除去菜单）
 *   状态2:显示导航和订单，账户，个人信息和菜单，（菜单里只显示下载，语言，汇率，不显示导航和订单，账户）
 *   状态3:显示导航和个人信息和菜单，（菜单显示下载，语言，汇率，订单，账户，不显示导航）
 *   状态4:显示个人信息和菜单，（菜单显示导航，下载，语言，汇率，订单，账户）
 * 未登录状态4种
 *   logo始终展示
 *   状态1:显示全部图标（除去菜单，没有订单，账户，个人信息替换的是登录注册按钮）
 *   状态2:显示导航和登录注册按钮和菜单，（菜单里只显示下载，语言，汇率）
 *   状态3:显示导航和菜单（菜单里只显示登录注册按钮和下载，语言，汇率）
 *   状态4:显示菜单（菜单显示登录注册按钮和导航，下载，语言，汇率）
 * 只取前三种的长度都不满足则用状态4;
 * 状态 key navStatus
 */

const regexHeader = /headers\d/;
const headerGa = (e) => {
  // 捕获冒泡，统一上报，后续上报新标准统一了再统一处理
  const { target } = e;
  if (target) {
    let useNode = null;
    if (target.getAttribute('data-modid')) {
      useNode = target;
    } else {
      useNode = getGaElement(target, 'data-ga');
    }
    if (useNode) {
      const modid = useNode.getAttribute('data-modid') || '';
      const idx = useNode.getAttribute('data-idx') || 0;
      if (modid) {
        // 可上报新格式
        const map = {
          siteid: 'kucoinWeb',
          pageid: 'homepage',
          modid,
          eleid: Number(idx),
        };
        if (regexHeader.test(modid)) {
          // 头部导航需要上报id
          const id = useNode.getAttribute('data-ga') || '';
          map.id = id;
        }
        gaClickNew('eleClickCollectionV1', map);
        if (includes(['person', 'assets', 'orders', 'appDowload'], modid)) {
          kcsensorsClick([modid, String(idx || 1)]);
        }
      }
    }
  }
};

const useStyles = () => {
  return {
    root: `
      position: relative;
      z-index: 100;
      width: 100%;
      & .logo {
        vertical-align: middle;
      }
    `,
    navbar: `
      width: 100%;
      height: 80px;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      @media screen and (max-width: 768px) {
        height: 60px;
      }
    `,
    headerStart: `
      position: relative;
      flex: auto;
      transition: all 0.3s;
    `,
  };
};
let hasRecived = false;
export default (props) => {
  const {
    theme: themeType, // light dark
    transparent,
    currentLang,
    KUCOIN_HOST, // kucoin主站地址
    TRADE_HOST, // 交易地址
    KUMEX_HOST, // kumex地址
    KUMEX_BASIC_HOST, // KuMEX简约版地址
    SANDBOX_HOST, // 沙盒地址
    FASTCOIN_HOST, // 一键买币地址
    MAINSITE_API_HOST, // kucoin主站API地址
    POOLX_HOST, // pool-x地址
    KUCOIN_HOST_CHINA, //  kucoin国内站地址
    LANDING_HOST, // 流量落地页地址
    userInfo = {},
  } = props;
  const theme = useTheme();
  const isSub = checkIsSub(userInfo);
  const rv = useResponsive();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showMenuDrawer, setShowMenuDrawer] = useState(false);
  const [showI18nDrawer, setShowI18nDrawer] = useState(false);
  const [isNav, setIsNav] = useState(false);
  const [i18nType, setI18nType] = useState('lang');
  const [status, setStatus] = useState(1);
  const { navList } = useSelector((state) => state[headerModal]);
  const navLinkRef = useRef(null);
  const signsRef = useRef(null);

  // 未登陆
  const widths = useRef({
    navLink: DEFAULT_NAVWIDTH_MAP.NAV_LINK,
    signs: DEFAULT_NAVWIDTH_MAP.SIGNS,
    actionBox: DEFAULT_NAVWIDTH_MAP.ACTION_BOX,
  }); // 默认宽度

  const geNavStatus = useCallback(
    (propsWidth) => {
      const winWidth = propsWidth - DEFAULT_NAVWIDTH_MAP.INNER_OFFSET;
      if (widths.current.navLink + widths.current.signs + widths.current.actionBox < winWidth) {
        setStatus(1);
      } else if (
        widths.current.navLink + widths.current.signs + DEFAULT_NAVWIDTH_MAP.MARGIN <
        winWidth
      ) {
        setStatus(2);
      } else if (widths.current.navLink + DEFAULT_NAVWIDTH_MAP.MARGIN < winWidth) {
        setStatus(3);
      } else {
        setStatus(4);
      }
    },
    [widths],
  );

  useEffect(() => {
    if (hasRecived) return;
    if (navLinkRef && navLinkRef.current) {
      const width = navLinkRef.current.offsetWidth;
      widths.current.navLink =
        width < DEFAULT_NAVWIDTH_MAP.NAV_LINK ? DEFAULT_NAVWIDTH_MAP.NAV_LINK : width;
    }
    if (!userInfo && signsRef && signsRef.current) {
      widths.current.signs = signsRef.current.offsetWidth;
    }
    if (navList.length) {
      hasRecived = true;
    }
  }, [navList, userInfo]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(
      throttle((entries) => {
        const {
          contentRect: { width },
        } = entries[0] || {};
        geNavStatus(width);
      }, 300),
    );
    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, [geNavStatus]);

  useEffect(() => {
    geNavStatus(document.body.clientWidth);
  }, [navList]);

  const hostConfig = {
    KUCOIN_HOST,
    TRADE_HOST,
    KUMEX_HOST,
    SANDBOX_HOST,
    MAINSITE_API_HOST,
    FASTCOIN_HOST,
    KUMEX_BASIC_HOST,
    POOLX_HOST,
    KUCOIN_HOST_CHINA,
    LANDING_HOST,
  };
  const maintenanceConfig = {
    pathname: props.pathname,
    maintenance: props.maintenance,
    maintainancePath: props.maintainancePath,
    showMaintenance: props.showMaintenance,
    onCloseMaintenance: props.onCloseMaintenance,
  };
  useEffect(() => {
    dispatch({
      type: `${namespace}/fetch`,
      payload: { currentLang, hostConfig, componentsToload: ['cms.common'] },
    });
  }, [currentLang]);

  const handleShowDrawer = (type, state, i18nTp) => {
    if (type === 'menu') {
      setIsNav(true);
      setShowMenuDrawer(state);
    } else if (type === 'i18n') {
      setShowI18nDrawer(state);
      i18nTp && setI18nType(i18nTp);
    } else if (type === 'user') {
      setIsNav(false);
      setShowMenuDrawer(state);
    }
  };

  useEffect(() => {
    dispatch({ type: `${headerModal}/pullLangList` });
  }, []);

  useEffect(() => {
    if (status === 1) {
      setShowI18nDrawer(false);
      setShowMenuDrawer(false);
    }
  }, [status]);

  return (
    <ClassNames>
      {({ css }) => (
        <>
          <LoadStyle run={['cms.common']} lang={currentLang} />
          <div className={css(classes.root)}>
            <Headroom transparent={transparent} theme={themeType} {...maintenanceConfig}>
              <Nav onClick={headerGa}>
                <div className={clsx(css(classes.navbar), css(classes.headerStart))}>
                  <Links ref={navLinkRef}>
                    <LogoLink
                      lang={currentLang}
                      href={queryPersistence.formatUrlWithStore(`${KUCOIN_HOST}`)}
                    >
                      <CmsComs.Load run="com.newheader.logo" />
                    </LogoLink>
                    {status === 4 ? null : (
                      <NavLinks id="hook_nav_link" data-lang={currentLang}>
                        <NavLink {...props} hostConfig={hostConfig} isSub={isSub} />
                      </NavLinks>
                    )}
                  </Links>
                  <NavUserDom id="hook_nav_user">
                    <CmsComs.Load run="com.header.navuser.left" combine lang={currentLang} />
                    <NavUser
                      {...props}
                      hostConfig={hostConfig}
                      isSub={isSub}
                      handleShowDrawer={handleShowDrawer}
                      navStatus={status}
                      signsRef={signsRef}
                    />
                    {status !== 1 ? (
                      <MenuBox data-lang={currentLang}>
                        <MenuOutlined
                          onClick={() => handleShowDrawer('menu', true)}
                          size={24}
                          color={theme.colors.text}
                          style={{ cursor: 'pointer' }}
                        />
                      </MenuBox>
                    ) : null}
                  </NavUserDom>
                </div>
              </Nav>
            </Headroom>
          </div>
          <MenuDrawer
            rv={rv}
            show={showMenuDrawer}
            onClose={() => handleShowDrawer('menu', false)}
            {...props}
            hostConfig={hostConfig}
            isSub={isSub}
            handleShowDrawer={handleShowDrawer}
            isNav={isNav}
            navStatus={status}
          />
          <I18nDrawer
            show={showI18nDrawer}
            onClose={() => handleShowDrawer('i18n', false)}
            {...props}
            hostConfig={hostConfig}
            isSub={isSub}
            type={i18nType}
            theme={themeType}
          />
        </>
      )}
    </ClassNames>
  );
};
